/**
 * Horizontal viewport-overflow compensation for fixed-position widget UI.
 *
 * `position: fixed` anchors to the layout viewport (`window.innerWidth`).
 * When a host page has horizontal overflow (e.g. an unclipped carousel
 * track), mobile browsers expand the layout viewport past the visible
 * area, so a `right: 20px` launcher lands off-screen to the right.
 *
 * We publish the delta between the layout viewport and the visible
 * client area as a CSS variable on the light-DOM mount host. Custom
 * properties inherit across the shadow boundary (they are excluded
 * from the `:host { all: initial }` reset), so fixed elements inside
 * the shadow root can add the delta to their `right` offset and pull
 * themselves back into view (see `floatingAnchor.js`).
 */

export const VIEWPORT_OVERFLOW_X_VAR = '--ometrics-viewport-overflow-x'

/** host -> cleanup fn, so start/stop are idempotent per host. */
const hostCleanups = new WeakMap()

function computeOverflowX() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 0
  }
  const layout = window.innerWidth || 0
  const visible = document.documentElement?.clientWidth || 0
  if (!layout || !visible) return 0
  return Math.max(0, layout - visible)
}

/**
 * Begin publishing `--ometrics-viewport-overflow-x` on `host` and keep
 * it fresh across resize / scroll / orientation changes.
 *
 * @param {Element | null | undefined} host light-DOM widget mount host
 */
export function startViewportOverflowAnchor(host) {
  if (!host || host.nodeType !== 1) return
  if (typeof window === 'undefined') return
  if (hostCleanups.has(host)) return

  let rafId = null

  const apply = () => {
    rafId = null
    host.style.setProperty(VIEWPORT_OVERFLOW_X_VAR, `${computeOverflowX()}px`)
  }

  const schedule = () => {
    if (rafId != null) return
    if (typeof requestAnimationFrame === 'function') {
      rafId = requestAnimationFrame(apply)
    } else {
      apply()
    }
  }

  window.addEventListener('resize', schedule)
  window.addEventListener('scroll', schedule, { passive: true })
  window.addEventListener('orientationchange', schedule)
  const visualViewport = window.visualViewport ?? null
  visualViewport?.addEventListener('resize', schedule)
  visualViewport?.addEventListener('scroll', schedule)

  hostCleanups.set(host, () => {
    if (rafId != null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(rafId)
    }
    rafId = null
    window.removeEventListener('resize', schedule)
    window.removeEventListener('scroll', schedule)
    window.removeEventListener('orientationchange', schedule)
    visualViewport?.removeEventListener('resize', schedule)
    visualViewport?.removeEventListener('scroll', schedule)
    host.style.removeProperty(VIEWPORT_OVERFLOW_X_VAR)
  })

  apply()
}

/**
 * Remove listeners and the CSS variable published by
 * {@link startViewportOverflowAnchor}. Safe to call on hosts that were
 * never started.
 *
 * @param {Element | null | undefined} host
 */
export function stopViewportOverflowAnchor(host) {
  if (!host) return
  const cleanup = hostCleanups.get(host)
  if (!cleanup) return
  hostCleanups.delete(host)
  cleanup()
}
